import Airtable from 'airtable';
import { Developer } from '../types/developer';

// Initialize Airtable
const getBase = () => {
  const apiKey = process.env.REACT_APP_AIRTABLE_API_KEY;
  const baseId = process.env.REACT_APP_AIRTABLE_BASE_ID;
  
  if (!apiKey || !baseId || apiKey === 'patXXXXXXXXXXXXXX' || baseId === 'appXXXXXXXXXXXXXX') {
    const missingVars = [];
    if (!apiKey || apiKey === 'patXXXXXXXXXXXXXX') missingVars.push('REACT_APP_AIRTABLE_API_KEY');
    if (!baseId || baseId === 'appXXXXXXXXXXXXXX') missingVars.push('REACT_APP_AIRTABLE_BASE_ID');
    
    throw new Error(`Airtable not configured: Missing or invalid ${missingVars.join(', ')}. Create a .env file in the project directory with your Airtable credentials. See env.example for template.`);
  }
  
  return new Airtable({ apiKey }).base(baseId);
};

const TABLE_NAME = 'Developers';

// Cache for storing fetched data
let developersCache: Developer[] | null = null;
let lastFetchTime: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Safely extract string value from AirTable field
 */
const safeGetString = (record: any, fieldName: string): string => {
  try {
    const value = record.get(fieldName);
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'object' && value.value !== undefined) {
      return String(value.value || '');
    }
    if (typeof value === 'object' && value.state !== undefined) {
      return String(value.state || '');
    }
    return String(value);
  } catch (error) {
    console.warn(`Error getting field ${fieldName}:`, error);
    return '';
  }
};

/**
 * Safely extract number value from AirTable field  
 */
const safeGetNumber = (record: any, fieldName: string): number => {
  try {
    const value = record.get(fieldName);
    if (value === null || value === undefined) {
      return 0;
    }
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'object' && value.value !== undefined) {
      return Number(value.value) || 0;
    }
    const numValue = Number(value);
    return isNaN(numValue) ? 0 : numValue;
  } catch (error) {
    console.warn(`Error getting number field ${fieldName}:`, error);
    return 0;
  }
};

/**
 * Safely extract array value from AirTable field
 */
const safeGetArray = (record: any, fieldName: string): string[] => {
  try {
    const value = record.get(fieldName);
    if (Array.isArray(value)) {
      return value.map(v => String(v));
    }
    if (value && typeof value === 'string') {
      return [value];
    }
    return [];
  } catch (error) {
    console.warn(`Error getting array field ${fieldName}:`, error);
    return [];
  }
};

/**
 * Safely extract experience level from AirTable field
 */
const safeGetExperienceLevel = (record: any, fieldName: string): 'Junior' | 'Mid-level' | 'Senior' | 'Lead' => {
  const value = safeGetString(record, fieldName);
  const validLevels = ['Junior', 'Mid-level', 'Senior', 'Lead'];
  return validLevels.includes(value) ? value as any : 'Mid-level';
};

/**
 * Safely extract availability from AirTable field
 */
const safeGetAvailability = (record: any, fieldName: string): 'Available' | 'Busy' | 'Not Available' => {
  const value = safeGetString(record, fieldName);
  const validAvailability = ['Available', 'Busy', 'Not Available'];
  return validAvailability.includes(value) ? value as any : 'Not Available';
};

/**
 * Safely extract English level from AirTable field
 */
const safeGetEnglishLevel = (record: any, fieldName: string): 'B1' | 'B2' | 'C1' | 'C2' | 'Native' => {
  const value = safeGetString(record, fieldName);
  const validLevels = ['B1', 'B2', 'C1', 'C2', 'Native'];
  return validLevels.includes(value) ? value as any : 'B1';
};

/**
 * Safely extract status from AirTable field
 */
const safeGetStatus = (record: any, fieldName: string): 'Active' | 'Inactive' | 'Under Review' => {
  const value = safeGetString(record, fieldName);
  const validStatuses = ['Active', 'Inactive', 'Under Review'];
  return validStatuses.includes(value) ? value as any : 'Inactive';
};

/**
 * Safely extract timezone from AirTable field
 */
const safeGetTimezone = (record: any, fieldName: string): 'UTC+2' | 'UTC+3' => {
  const value = safeGetString(record, fieldName);
  const validTimezones = ['UTC+2', 'UTC+3'];
  return validTimezones.includes(value) ? value as any : 'UTC+2';
};

/**
 * Transform Airtable record to Developer interface
 */
const transformRecord = (record: any): Developer => {
  try {
    console.log('Transforming AirTable record:', record.id);
    
    const developer = {
      id: record.id,
      name: safeGetString(record, 'Name'),
      email: safeGetString(record, 'Email'),
      photo: record.get('Photo')?.[0]?.url || '',
      location: safeGetString(record, 'Location') || 'Ukraine',
      type: safeGetString(record, 'Type') || 'Full-stack',
      skills: safeGetArray(record, 'Skills'),
      experienceLevel: safeGetExperienceLevel(record, 'Experience Level'),
      hourlyRate: safeGetNumber(record, 'Hourly Rate'),
      availability: safeGetAvailability(record, 'Availability'),
      englishLevel: safeGetEnglishLevel(record, 'English Level'),
      bio: safeGetString(record, 'Bio'),
      bioSummary: safeGetString(record, 'Bio Summary'),
      selectedJobExperienceAI: safeGetString(record, 'Selected Job Experience (AI)'),
      portfolioUrl: safeGetString(record, 'Portfolio URL'),
      linkedInUrl: safeGetString(record, 'LinkedIn URL'),
      githubUrl: safeGetString(record, 'GitHub URL'),
      cvFile: record.get('CV File')?.[0]?.url || '',
      status: safeGetStatus(record, 'Status'),
      dateAdded: new Date(record.get('Date Added') || Date.now()),
      yearsExperience: safeGetNumber(record, 'Years Experience'),
      timeZone: safeGetTimezone(record, 'Time Zone')
    };
    
    console.log('Transformed developer:', {
      id: developer.id,
      name: developer.name,
      bioSummary: developer.bioSummary,
      skills: developer.skills,
      hasPhoto: !!developer.photo
    });
    
    return developer;
  } catch (error) {
    console.error('Error transforming AirTable record:', error, record);
    // Return a safe fallback object
    return {
      id: record.id || 'unknown',
      name: 'Unknown Developer',
      email: '',
      photo: '',
      location: 'Ukraine',
      type: 'Full-stack',
      skills: [],
      experienceLevel: 'Mid-level' as const,
      hourlyRate: 0,
      availability: 'Not Available' as const,
      englishLevel: 'B1' as const,
      bio: '',
      bioSummary: '',
      selectedJobExperienceAI: '',
      portfolioUrl: '',
      linkedInUrl: '',
      githubUrl: '',
      cvFile: '',
      status: 'Inactive' as const,
      dateAdded: new Date(),
      yearsExperience: 0,
      timeZone: 'UTC+2' as const
    };
  }
};

/**
 * Fetch all active developers from Airtable
 */
export const fetchDevelopers = async (): Promise<Developer[]> => {
  try {
    // Check cache first
    const now = Date.now();
    if (developersCache && (now - lastFetchTime) < CACHE_DURATION) {
      return developersCache;
    }

    console.log('Fetching developers from Airtable...');
    
    const base = getBase();
    const records = await base(TABLE_NAME)
      .select({
        filterByFormula: "{Status} = 'Active'",
        sort: [{ field: 'Date Added', direction: 'desc' }]
      })
      .all();

    const developers = records.map(transformRecord);
    
    // Update cache
    developersCache = developers;
    lastFetchTime = now;
    
    console.log(`Fetched ${developers.length} active developers`);
    return developers;
  } catch (error) {
    console.error('Error fetching developers:', error);
    throw new Error('Failed to fetch developers from Airtable');
  }
};

/**
 * Fetch a single developer by ID
 */
export const fetchDeveloperById = async (id: string): Promise<Developer | null> => {
  try {
    const base = getBase();
    const record = await base(TABLE_NAME).find(id);
    return transformRecord(record);
  } catch (error) {
    console.error('Error fetching developer by ID:', error);
    return null;
  }
};

/**
 * Search developers by skills
 */
export const searchDevelopersBySkills = async (skills: string[]): Promise<Developer[]> => {
  try {
    if (skills.length === 0) {
      return await fetchDevelopers();
    }

    const skillFilter = skills.map(skill => `SEARCH('${skill}', ARRAYJOIN({Skills}))`).join(', ');
    const filterFormula = `AND({Status} = 'Active', OR(${skillFilter}))`;

    const base = getBase();
    const records = await base(TABLE_NAME)
      .select({
        filterByFormula: filterFormula,
        sort: [{ field: 'Date Added', direction: 'desc' }]
      })
      .all();

    return records.map(transformRecord);
  } catch (error) {
    console.error('Error searching developers by skills:', error);
    throw new Error('Failed to search developers by skills');
  }
};

/**
 * Filter developers by multiple criteria
 */
export const filterDevelopers = async (filters: {
  skills?: string[];
  experienceLevel?: string[];
  hourlyRateRange?: { min: number; max: number };
  availability?: string[];
  englishLevel?: string[];
}): Promise<Developer[]> => {
  try {
    const allDevelopers = await fetchDevelopers();
    
    return allDevelopers.filter(developer => {
      // Filter by skills
      if (filters.skills && filters.skills.length > 0) {
        const hasRequiredSkills = filters.skills.some(skill => 
          developer.skills.includes(skill)
        );
        if (!hasRequiredSkills) return false;
      }

      // Filter by experience level
      if (filters.experienceLevel && filters.experienceLevel.length > 0) {
        if (!filters.experienceLevel.includes(developer.experienceLevel)) {
          return false;
        }
      }

      // Filter by hourly rate range
      if (filters.hourlyRateRange) {
        const { min, max } = filters.hourlyRateRange;
        if (developer.hourlyRate < min || developer.hourlyRate > max) {
          return false;
        }
      }

      // Filter by availability
      if (filters.availability && filters.availability.length > 0) {
        if (!filters.availability.includes(developer.availability)) {
          return false;
        }
      }

      return true;
    });
  } catch (error) {
    console.error('Error filtering developers:', error);
    throw new Error('Failed to filter developers');
  }
};

/**
 * Clear the cache (useful for testing or when data needs to be refreshed)
 */
export const clearCache = (): void => {
  developersCache = null;
  lastFetchTime = 0;
};

/**
 * Get cache status
 */
export const getCacheStatus = (): { hasCache: boolean; age: number } => {
  const now = Date.now();
  return {
    hasCache: developersCache !== null,
    age: lastFetchTime > 0 ? now - lastFetchTime : 0
  };
}; 